import BorderCard from "./components/BorderCard";
import CardTitle from "./components/CardTitle";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useUpdateCampaign,
  useUpdateCampaignNotificationEmail,
  useAddCampaignAsset,
  useDeleteCampaignAsset,
} from "../../hooks/useCampaign";
import { nextContentGeneration } from "@/features/campaigns/utils/stepContentMode";
import { useForm } from "react-hook-form";
import { campaignSettingSchema } from "./components/Settings/campaignSettingSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import CampaignSettingForm from "./components/Settings/CampaignSettingForm";
import {
  DEFAULT_SCHEDULE,
  industryLevelTriggers as industryLevelTriggerOptions,
  triggers as companyLevelTriggerOptions,
} from "@/shared/utils/utils";
import useUnsavedChangesWarning from "@/shared/hooks/useUnsavedChangesWarning";
import { normalizeCompanySizeList } from "@/shared/utils/onboardingConfig";

const splitLegacyTriggers = (legacy = []) => {
  const values = Array.isArray(legacy) ? legacy : [];
  const companySet = new Set(companyLevelTriggerOptions);
  const industrySet = new Set(industryLevelTriggerOptions);

  return {
    companyLevelSalesTrigger: values.filter((v) => companySet.has(v)),
    industryLevelTrigger: values.filter((v) => industrySet.has(v)),
  };
};

const normalizeCampaignServices = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map(String)
          .map((s) => s.trim())
          .filter(Boolean);
      }
    } catch {
      /* plain string */
    }
    const t = raw.trim();
    return t ? [t] : [];
  }
  return [];
};

const SettingContent = ({ campaignId, campaignData }) => {
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [showTargetAudienceModal, setShowTargetAudienceModal] = useState(false);
  const [showMessageStyleModal, setShowMessageStyleModal] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isSavingNotificationEmail, setIsSavingNotificationEmail] =
    useState(false);
  const [isSavingPitch, setIsSavingPitch] = useState(false);
  const [isSavingAudience, setIsSavingAudience] = useState(false);
  const [isSavingMessageStyle, setIsSavingMessageStyle] = useState(false);
  const [schedule, setSchedule] = useState(() =>
    DEFAULT_SCHEDULE.map((s) => ({ ...s })),
  );
  const [_isSavingSchedule, setIsSavingSchedule] = useState(false);
  const { mutate: updateCampaign, isPending: isUpdatingCampaign } = useUpdateCampaign(campaignId);
  const [showMessageTemplatesModal, setShowMessageTemplatesModal] = useState(false);
  const handleSaveMessageTemplates = useCallback(
    (messageTemplates) =>
      updateCampaign(
        { campaignId, campaignData: { messageTemplates } },
        { onSuccess: () => setShowMessageTemplatesModal(false) },
      ),
    [campaignId, updateCampaign],
  );
  const handleContentGenerationChange = useCallback(
    (aiOn) =>
      updateCampaign({
        campaignId,
        campaignData: { contentGeneration: nextContentGeneration(aiOn) },
      }),
    [campaignId, updateCampaign],
  );
  const { mutate: updateNotificationEmail } =
    useUpdateCampaignNotificationEmail(campaignId);
  const { mutateAsync: addAsset } = useAddCampaignAsset();
  const { mutateAsync: deleteAsset } = useDeleteCampaignAsset();
  const [companyAssets, setCompanyAssets] = useState([]);
  const [pitchData, setPitchData] = useState({
    painPointsSolved: "",
    valueProposition: "",
    companyLevelSalesTrigger: [],
    industryLevelTrigger: [],
    companyAssets: [],
  });
  const [audienceData, setAudienceData] = useState({
    companySize: [],
    targetIndustries: [],
    roles: [],
    businessLocations: [],
    idealCustomer: "",
  });
  const [messageStyle, setMessageStyle] = useState([]);
  // Initialize form with react-hook-form
  const { register, control, watch, setValue, formState, reset, trigger } =
    useForm({
      mode: "onChange",
      resolver: zodResolver(campaignSettingSchema),
      defaultValues: {
        campaignTitle: "",
        campaignLanguage: "English (India)",
        emailDomain: "",
        services: [],
        notificationEmail: "",
      },
    });

  // Watch form values
  const campaignTitle = watch("campaignTitle");
  const notificationEmail = watch("notificationEmail");
  const servicesField = watch("services");
  const defaultCampaignTitle = campaignData?.name || "";
  const defaultNotificationEmail = campaignData?.notification_email || "";
  const servicesSaveTimerRef = useRef(null);

  useUnsavedChangesWarning(formState.isDirty);

  // Update form values when campaignData loads
  useEffect(() => {
    if (campaignData) {
      const rawLang = campaignData?.campaign_language;
      const campaignLanguage =
        typeof rawLang === "string"
          ? (() => {
            try {
              const parsed = JSON.parse(rawLang);
              return parsed ?? "English (India)";
            } catch {
              return rawLang || "English (India)";
            }
          })()
          : rawLang || "English (India)";

      reset({
        campaignTitle: campaignData.name || "",
        campaignLanguage,
        emailDomain: campaignData?.email_domains || [],
        services: normalizeCampaignServices(campaignData.services),
        notificationEmail: campaignData.notification_email || "",
      });
    }
  }, [campaignData, reset]);

  useEffect(() => {
    return () => {
      if (servicesSaveTimerRef.current) {
        clearTimeout(servicesSaveTimerRef.current);
      }
    };
  }, []);

  const handleServicesChange = useCallback(
    (nextServices) => {
      if (servicesSaveTimerRef.current) {
        clearTimeout(servicesSaveTimerRef.current);
      }
      servicesSaveTimerRef.current = setTimeout(() => {
        servicesSaveTimerRef.current = null;
        const list = Array.isArray(nextServices) ? nextServices : [];
        updateCampaign({
          campaignId,
          campaignData: { campaignService: list },
        });
      }, 450);
    },
    [campaignId, updateCampaign],
  );

  // Hydrate schedule from campaignData when available
  useEffect(() => {
    const raw = campaignData?.schedule ?? campaignData?.campaign_schedule;
    if (raw && Array.isArray(raw) && raw.length > 0) {
      setSchedule(
        DEFAULT_SCHEDULE.map((def, idx) => {
          const item = raw[idx] ?? raw.find((r) => r.day === def.day);
          if (!item) return { ...def };
          return {
            id: item.id ?? def.id,
            day: item.day ?? def.day,
            selected: item.selected ?? item.is_active ?? def.selected,
            timeFrom: item.timeFrom ?? item.time_from ?? def.timeFrom,
            timeTo: item.timeTo ?? item.time_to ?? def.timeTo,
          };
        }),
      );
    }
  }, [campaignData?.schedule, campaignData?.campaign_schedule]);

  // Handlers
  const handleEditClickPitch = useCallback(() => {
    setShowPitchModal(true);
  }, []);

  const handleEditClickTargetAudience = useCallback(() => {
    setShowTargetAudienceModal(true);
  }, []);

  const handleEditClickMessageStyle = useCallback(() => {
    setShowMessageStyleModal(true);
  }, []);

  const handleClosePitchModal = useCallback(() => {
    setShowPitchModal(false);
  }, []);

  const handleCloseTargetAudienceModal = useCallback(() => {
    setShowTargetAudienceModal(false);
  }, []);

  const handleCloseMessageStyleModal = useCallback(() => {
    setShowMessageStyleModal(false);
  }, []);

  const handleSaveTitle = useCallback(async () => {
    setIsSavingTitle(true);
    try {
      updateCampaign({
        campaignId,
        campaignData: {
          campaignName: campaignTitle,
        },
      });
      setIsSavingTitle(false);
    } catch (error) {
      console.error("Failed to save title:", error);
    } finally {
      setIsSavingTitle(false);
    }
  }, [campaignTitle, campaignId, updateCampaign]);

  const handleSaveNotificationEmail = useCallback(async () => {
    const isValid = await trigger("notificationEmail");
    if (!isValid) return;

    setIsSavingNotificationEmail(true);
    updateNotificationEmail(
      {
        campaignId,
        notificationEmail: notificationEmail?.trim() || "",
      },
      {
        onSettled: () => setIsSavingNotificationEmail(false),
      },
    );
  }, [campaignId, notificationEmail, trigger, updateNotificationEmail]);

  // Refs for submit handlers
  const pitchSubmitHandlerRef = useRef(null);
  const audienceSubmitHandlerRef = useRef(null);

  const registerPitchSubmitHandler = useCallback((handler) => {
    pitchSubmitHandlerRef.current = handler;
  }, []);

  const registerAudienceSubmitHandler = useCallback((handler) => {
    audienceSubmitHandlerRef.current = handler;
  }, []);

  const handleAssetsChange = useCallback((assets) => {
    setCompanyAssets(assets);
  }, []);

  // Update pitch and audience data when campaignData loads
  useEffect(() => {
    if (campaignData) {
      // Set pitch data
      if (campaignData.define_pitch) {
        const companyLevelSalesTrigger =
          campaignData.define_pitch?.companyLevelSalesTrigger;
        const industryLevelTrigger =
          campaignData.define_pitch?.industryLevelTrigger;
        const fallbackSplit = splitLegacyTriggers(
          campaignData.define_pitch?.salesTrigger || [],
        );
        setPitchData({
          painPointsSolved: campaignData.define_pitch.painPoints || "",
          valueProposition: campaignData.define_pitch.valueProposition || "",
          companyLevelSalesTrigger:
            companyLevelSalesTrigger || fallbackSplit.companyLevelSalesTrigger,
          industryLevelTrigger:
            industryLevelTrigger || fallbackSplit.industryLevelTrigger,
          signalTriggerConfig:
            campaignData.define_pitch?.signalTriggerConfig ||
            campaignData.define_pitch?.signal_trigger_config ||
            null,
          companyAssetsDetails:
            campaignData.define_pitch.companyAssetsDetails || [],
          companyAssets:
            campaignData.assets ||
            campaignData.define_pitch?.companyAssets ||
            [],
        });
        const rawAssets =
          campaignData.assets || campaignData.define_pitch?.companyAssets || [];
        const normalized = Array.isArray(rawAssets)
          ? rawAssets.map((a) =>
            typeof a === "object" && a?.id
              ? {
                ...a,
                file_name: a.file_name || a.filename || a.name,
                context: a.context ?? "",
              }
              : a,
          )
          : [];
        setCompanyAssets(normalized);
      }

      // Set audience data
      if (campaignData.target_audience) {
        setAudienceData({
          companySize: normalizeCompanySizeList(
            campaignData.target_audience.companySize,
          ),
          targetIndustries:
            campaignData.target_audience.targetIndutries ||
            campaignData.target_audience.targetIndustries ||
            [],
          roles: campaignData.target_audience.roles || [],
          businessLocations:
            campaignData.target_audience.businessLocation || [],
          idealCustomer: campaignData.target_audience.campaignDescription || "",
        });
      }

      const storedStyles =
        campaignData.messageStyle ??
        campaignData.target_audience?.messageStyle ??
        [];
      setMessageStyle(
        Array.isArray(storedStyles) ? storedStyles.filter(Boolean) : [],
      );
    }
  }, [campaignData]);

  // Check if title has changed from default
  const hasTitleChanged = campaignTitle !== defaultCampaignTitle;
  const hasNotificationEmailChanged =
    (notificationEmail ?? "") !== defaultNotificationEmail;
  const canSaveNotificationEmail =
    hasNotificationEmailChanged && !formState.errors.notificationEmail;

  const handleCampaignLanguageChange = useCallback(
    (campaignLanguage) => {
      updateCampaign({
        campaignId,
        campaignData: { campaignLanguage: JSON.stringify(campaignLanguage) },
      });
    },
    [campaignId, updateCampaign],
  );

  const handleEmailDomainChange = useCallback(
    (emailDomains) => {
      const domains = Array.isArray(emailDomains)
        ? emailDomains
        : [emailDomains];
      updateCampaign({
        campaignId,
        campaignData: { emailDomains: JSON.stringify(domains) },
      });
    },
    [campaignId, updateCampaign],
  );

  const _handleScheduleToggle = useCallback((id, selected) => {
    setSchedule((prev) =>
      prev.map((s) => (s.id === id ? { ...s, selected } : s)),
    );
  }, []);

  const _handleScheduleTimeChange = useCallback(
    (id, { startTime, endTime }) => {
      setSchedule((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, timeFrom: startTime, timeTo: endTime } : s,
        ),
      );
    },
    [],
  );

  const _handleSaveSchedule = useCallback(() => {
    setIsSavingSchedule(true);
    updateCampaign(
      {
        campaignId,
        campaignData: {
          campaignSchedule: schedule.map((s) => ({
            day: s.day,
            selected: s.selected,
            timeFrom: s.timeFrom,
            timeTo: s.timeTo,
            disabled: s.disabled,
          })),
        },
      },
      {
        onSettled: () => setIsSavingSchedule(false),
      },
    );
  }, [campaignId, schedule, updateCampaign]);

  const handlePitchSubmit = useCallback(
    async (data) => {
      setIsSavingPitch(true);
      try {
        const {
          painPointsSolved,
          valueProposition,
          companyLevelSalesTrigger,
          industryLevelTrigger,
          signalTriggerConfig,
          companyAssetIds: keptIds = [],
          companyAssetsDetails: keptContexts = [],
          pendingDeletes = [],
          newFiles = [],
          newAssetDetails = [],
        } = data;

        // 1. Delete removed assets from server
        for (const assetId of pendingDeletes) {
          await deleteAsset({ assetId, suppressSuccessToast: true });
        }

        // 2. Upload new files to get IDs
        const newIds = [];
        const newContexts = [];
        for (const { file, context } of newFiles) {
          const fileObj = file?.file || file;
          if (fileObj instanceof File) {
            const res = await addAsset({
              assetData: fileObj,
              description: context || "",
            });
            const assetData = res?.data || res;
            if (assetData?.id) {
              newIds.push(assetData.id);
              newContexts.push(context || "");
            }
          }
        }

        const companyAssetIds = [...keptIds, ...newIds];
        const companyAssetsDetails = [
          ...keptContexts,
          ...newContexts,
          ...newAssetDetails,
        ];

        console.log("[SettingContent] pitch save payload", {
          attachedRows: companyAssetIds.length,
          detailRows: companyAssetsDetails.length,
          unattachedRows: newAssetDetails.length,
        });

        updateCampaign(
          {
            campaignId,
            campaignData: {
              definePitch: {
                painPoints: painPointsSolved,
                valueProposition,
                companyLevelSalesTrigger: companyLevelSalesTrigger || [],
                industryLevelTrigger: industryLevelTrigger || [],
                signalTriggerConfig: signalTriggerConfig || null,
                companyAssetIds,
                companyAssetsDetails,
              },
            },
          },
          {
            onSuccess: () => setShowPitchModal(false),
            onSettled: () => setIsSavingPitch(false),
          },
        );
      } catch (err) {
        console.error("Pitch save failed:", err);
        setIsSavingPitch(false);
      }
    },
    [campaignId, updateCampaign, addAsset, deleteAsset],
  );

  const handleTargetAudienceSubmit = useCallback(
    (data) => {
      setIsSavingAudience(true);
      updateCampaign(
        {
          campaignId,
          campaignData: {
            targetAudience: {
              companySize: normalizeCompanySizeList(data.companySize),
              targetIndustries: data.targetIndustries,
              roles: data.roles,
              businessLocation: data.businessLocations,
              campaignDescription: data.idealCustomer || "",
            },
          },
        },
        {
          onSuccess: () => setShowTargetAudienceModal(false),
          onSettled: () => setIsSavingAudience(false),
        },
      );
    },
    [campaignId, updateCampaign],
  );

  const handleAudienceSaveClick = useCallback(async () => {
    const result = await audienceSubmitHandlerRef.current?.();
    if (result?.success && result?.data) {
      handleTargetAudienceSubmit(result.data);
    }
  }, [handleTargetAudienceSubmit]);

  const handlePitchSaveClick = useCallback(async () => {
    const result = await pitchSubmitHandlerRef.current?.();
    if (result?.success && result?.data) {
      handlePitchSubmit(result.data);
    }
  }, [handlePitchSubmit]);

  const handleMessageStyleSubmit = useCallback(
    (styles) => {
      setIsSavingMessageStyle(true);
      const list = Array.isArray(styles) ? styles : [];
      updateCampaign(
        {
          campaignId,
          campaignData: {
            messageStyle: list,
            targetAudience: {
              companySize: normalizeCompanySizeList(audienceData.companySize),
              targetIndustries: audienceData.targetIndustries,
              roles: audienceData.roles,
              businessLocation: audienceData.businessLocations,
              campaignDescription: audienceData.idealCustomer || "",
              messageStyle: list,
            },
          },
        },
        {
          onSuccess: () => {
            setMessageStyle(list);
            setShowMessageStyleModal(false);
          },
          onSettled: () => setIsSavingMessageStyle(false),
        },
      );
    },
    [campaignId, audienceData, updateCampaign],
  );

  const handleMessageStyleSaveClick = useCallback(
    (styles) => {
      handleMessageStyleSubmit(styles);
    },
    [handleMessageStyleSubmit],
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-col items-stretch gap-4 lg:flex-row">
        {/* left side area */}
        <div className="flex min-h-0 w-full flex-col">
          {/* campaign settings */}
          <BorderCard>
            <CardTitle text={"Campaign Settings"} />
          </BorderCard>
          <BorderCard
            variant="bottom"
            className="h-[calc(100dvh-180px)]  min-h-0 overflow-hidden flex-col"
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <CampaignSettingForm
                campaignDetails={campaignData}
                register={register}
                control={control}
                watch={watch}
                setValue={setValue}
                formState={formState}
                errors={formState.errors}
                services={servicesField ?? []}
                onServicesChange={handleServicesChange}
                campaignTitle={campaignTitle}
                notificationEmail={notificationEmail}
                isSavingTitle={isSavingTitle}
                hasTitleChanged={hasTitleChanged}
                handleSaveTitle={handleSaveTitle}
                canSaveNotificationEmail={canSaveNotificationEmail}
                notificationEmailError={formState.errors.notificationEmail?.message}
                handleSaveNotificationEmail={handleSaveNotificationEmail}
                isSavingNotificationEmail={isSavingNotificationEmail}
                handleEditClickPitch={handleEditClickPitch}
                handleEditClickTargetAudience={handleEditClickTargetAudience}
                handleEditClickMessageStyle={handleEditClickMessageStyle}
                showPitchModal={showPitchModal}
                showTargetAudienceModal={showTargetAudienceModal}
                showMessageStyleModal={showMessageStyleModal}
                handleClosePitchModal={handleClosePitchModal}
                handleCloseTargetAudienceModal={handleCloseTargetAudienceModal}
                handleCloseMessageStyleModal={handleCloseMessageStyleModal}
                handleMessageStyleSaveClick={handleMessageStyleSaveClick}
                showMessageTemplatesModal={showMessageTemplatesModal}
                onEditMessageTemplates={() => setShowMessageTemplatesModal(true)}
                onCloseMessageTemplatesModal={() => setShowMessageTemplatesModal(false)}
                onSaveMessageTemplates={handleSaveMessageTemplates}
                isSavingMessageTemplates={isUpdatingCampaign}
                messageStyle={messageStyle}
                onContentGenerationChange={handleContentGenerationChange}
                isSavingMessageStyle={isSavingMessageStyle}
                pitchData={pitchData}
                audienceData={audienceData}
                registerPitchSubmitHandler={registerPitchSubmitHandler}
                registerAudienceSubmitHandler={registerAudienceSubmitHandler}
                onCampaignLanguageChange={handleCampaignLanguageChange}
                companyAssets={companyAssets}
                handleAssetsChange={handleAssetsChange}
                setCompanyAssets={setCompanyAssets}
                campaignId={campaignId}
                handleEmailDomainChange={handleEmailDomainChange}
                handleAudienceSaveClick={handleAudienceSaveClick}
                handlePitchSaveClick={handlePitchSaveClick}
                onPitchDataChange={(data) =>
                  setPitchData((prev) => ({ ...prev, ...data }))
                }
                setAudienceData={setAudienceData}
                isSavingAudience={isSavingAudience}
                isSavingPitch={isSavingPitch}
              />
            </div>
          </BorderCard>
        </div>

        {/* right side area */}
        {/* <div className="w-full lg:w-[41.5%] flex flex-col">
          <BorderCard>
            <CardTitle text={"Campaign Schedule"} />
          </BorderCard>
          <BorderCard variant="bottom" className="flex-1 min-h-0">
            <CampaignScheduleSection
              schedule={schedule}
              onToggle={handleScheduleToggle}
              onTimeChange={handleScheduleTimeChange}
              onSave={handleSaveSchedule}
              isSaving={isSavingSchedule}
            />
          </BorderCard>
        </div> */}
      </div>
    </div>
  );
};

export default SettingContent;
